<template>
	<AppLayout>
		<!-- Resume Test Banner (shown when test is in progress) -->
		<!-- Position below sticky nav with proper spacing -->
		<div v-if="showResumeBanner" class="sticky z-40 top-16">
			<ResumeTestBanner @resume="handleResumeTest" @dismiss="handleDismissSaved" />
		</div>

		<!-- Academic Design Landing Page -->
		<div class="min-h-screen bg-gradient-to-b from-blue-50 to-white">
			<!-- Hero Section - Two Column with SEO Optimization -->
			<section class="py-16 border-b border-blue-100 md:py-20">
				<div class="container px-4 mx-auto">
					<div class="grid items-center max-w-6xl grid-cols-1 gap-12 mx-auto lg:grid-cols-2">
						<!-- Left Column: Content -->
						<div>
							<div class="inline-block px-4 py-2 mb-6 bg-blue-100 rounded-full">
								<span class="text-sm font-semibold text-blue-700"
									>Kostenloser Basis Report • 90 Fragen • Sofort Ergebnis</span
								>
							</div>

							<!-- H1 with Primary Keyword -->
							<h1 class="mb-6 text-4xl font-bold leading-tight text-blue-900 md:text-5xl">
								Der tiefgehende <span class="text-green-600">Lerntypentest</span>: 90 gezielte
								Fragen – Ihr Lerntyp-Ergebnis sofort & gratis!
							</h1>

							<!-- Subline: Differentiation from Competition -->
							<p class="mb-8 text-lg font-semibold leading-relaxed text-blue-700">
								Kein oberflächlicher 5-Minuten-Quiz: Entwickelt nach aktuellen pädagogischen
								Standards für eine echte Analyse.
							</p>

							<!-- Main CTA - Most Visible Element -->
							<TrackClick
								event="cta_clicked"
								category="cta"
								cta-type="primary"
								cta-location="home_hero"
								page-section="home_hero"
								tag="button"
								class="inline-flex items-center px-10 py-5 mb-8 text-lg font-bold text-white transition-colors transform bg-green-600 rounded-lg shadow-xl hover:bg-green-700 hover:shadow-2xl hover:scale-105"
								@click="startOnboarding"
							>
								Test starten (Gratis-Ergebnis sichern)!
							</TrackClick>

							<!-- Trust Indicators with Keywords -->
							<div class="space-y-3 text-sm text-blue-700">
								<div class="flex items-center">
									<AppIcon name="heroicons-outline:check" class="w-5 h-5 mr-3 text-green-600" />
									<span>90 Fragen für echte Klarheit</span>
								</div>
								<div class="flex items-center">
									<AppIcon name="heroicons-outline:check" class="w-5 h-5 mr-3 text-green-600" />
									<span>Keine Registrierung erforderlich</span>
								</div>
								<div class="flex items-center">
									<AppIcon name="heroicons-outline:check" class="w-5 h-5 mr-3 text-green-600" />
									<span>Sofortige Ergebnisse</span>
								</div>
							</div>
						</div>

						<!-- Right Column: Sample Report Card -->
						<div class="hidden lg:block">
							<SampleReportCard />
						</div>
					</div>
				</div>
			</section>

			<!-- Mid-Fold: Quality Gap Section (Vester Criticism) -->
			<section class="py-16 bg-gradient-to-b from-white to-blue-50">
				<div class="container px-4 mx-auto">
					<div class="max-w-4xl mx-auto">
						<!-- Section B: The Value of 90 Questions -->
						<div class="mb-16">
							<h2 class="mb-6 text-3xl font-bold text-center text-blue-900">
								So liefert unser 90-Fragen-Test echte Klarheit
							</h2>
							<div class="grid grid-cols-1 gap-8 md:grid-cols-3">
								<div
									class="p-8 transition-colors border border-blue-200 rounded-lg bg-blue-50 hover:border-blue-300"
								>
									<div class="mb-3 text-3xl font-bold text-blue-600">Umfassend</div>
									<p class="text-blue-700">
										90 gezielte Fragen analysieren die Verarbeitung im Gehirn und gedankliche
										Verknüpfungen – nicht nur Sinnesorgane
									</p>
								</div>

								<div
									class="p-8 transition-colors border border-green-200 rounded-lg bg-green-50 hover:border-green-300"
								>
									<div class="mb-3 text-3xl font-bold text-green-600">Validiert</div>
									<p class="text-green-700">
										Entwickelt und geprüft von zertifizierten Lernpädagogen basierend auf aktuellen
										pädagogischen Standards
									</p>
								</div>

								<div
									class="p-8 transition-colors border border-blue-200 rounded-lg bg-blue-50 hover:border-blue-300"
								>
									<div class="mb-3 text-3xl font-bold text-blue-600">Präzise</div>
									<p class="text-blue-700">
										Detaillierte Analyse für bessere Noten, optimierte Klassenarbeiten-Vorbereitung
										und Lernstrategien
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- CTA Section: Ready to Start - Full Width -->
			<section class="py-16 bg-gradient-to-r from-blue-50 to-green-50">
				<div class="container px-4 mx-auto">
					<div class="max-w-4xl mx-auto">
						<ConversionElements
							headline="Bereit, den Lerntyp Ihres Kindes zu entdecken?"
							subheadline="Starten Sie jetzt den kostenlosen 90-Fragen-Test und erhalten Sie sofort Ihr personalisiertes Ergebnis."
							cta-text="Test jetzt starten"
							secondary-cta-text="Mehr erfahren"
							variant="gradient"
							background-color="bg-transparent"
							:trust-indicators="['Kostenlos', 'Keine Anmeldung', 'Sofort Ergebnisse']"
							show-urgency
							urgency-message="Über 15.000 Eltern haben bereits teilgenommen"
							@cta-click="startOnboarding"
							@secondary-cta-click="scrollToSection"
						/>
					</div>
				</div>
			</section>

			<!-- Continue with rest of content -->
			<section id="Lerntypen Test Mehrwert" class="py-16 bg-gradient-to-b from-white to-blue-50">
				<div class="container px-4 mx-auto">
					<div class="max-w-4xl mx-auto">
						<!-- Section C: The Unsolved Pain Point -->
						<div>
							<h2 class="mb-6 text-3xl font-bold text-center text-blue-900">
								Ihr Lerntyp ist nur der Anfang
							</h2>
							<div class="p-8 border border-green-200 rounded-lg bg-green-50">
								<p class="mb-4 font-semibold text-green-900">
									Das kostenlose Ergebnis liefert Ihnen den Lerntyp. Aber was dann?
								</p>
								<p class="mb-6 text-green-800">
									Viele Eltern und Schüler wissen nach dem Test immer noch nicht, wie sie ihre
									Lernschwierigkeiten ursachen konkret beheben. Die Lösung für bessere Noten,
									optimierte Klassenarbeiten-Vorbereitung und individuelle Lernstrategien liegt in
									den kaufbaren personalisierten Empfehlungen von zertifizierten Lernpädagogen.
								</p>
								<div class="p-4 bg-white border border-green-200 rounded">
									<p class="mb-2 font-semibold text-green-900">
										Nach dem kostenlosen Test erhalten Sie:
									</p>
									<ul class="space-y-2 text-green-800">
										<li class="flex items-start">
											<AppIcon name="heroicons-outline:check" class="w-5 h-5 mr-3 text-green-600" />
											<span>Ihr Lerntyp-Ergebnis (kostenlos)</span>
										</li>
										<li class="flex items-start">
											<AppIcon name="heroicons-outline:check" class="w-5 h-5 mr-3 text-green-600" />
											<span>Option auf professionelle Analyse mit individuellen Empfehlungen</span>
										</li>
										<li class="flex items-start">
											<AppIcon name="heroicons-outline:check" class="w-5 h-5 mr-3 text-green-600" />
											<span
												>Konkrete Strategien für Schulstoff-Verarbeitung und Klassenarbeiten</span
											>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Assessment Components Section -->
			<section class="py-16 bg-gradient-to-b from-blue-50 to-white">
				<div class="container px-4 mx-auto">
					<div class="max-w-4xl mx-auto">
						<h2 class="mb-12 text-3xl font-bold text-center text-blue-900">
							Analysierte Dimensionen
						</h2>

						<div class="space-y-6">
							<div
								class="p-6 transition-colors bg-white border border-blue-200 rounded-lg hover:border-blue-300"
							>
								<h3 class="mb-2 text-lg font-semibold text-blue-900">Lerntypenanalyse</h3>
								<p class="text-blue-700">
									Identifikation visueller, auditiver und kinästhetischer Lernpräferenzen zur
									Optimierung von Lernmethoden
								</p>
							</div>

							<div
								class="p-6 transition-colors bg-white border border-green-200 rounded-lg hover:border-green-300"
							>
								<h3 class="mb-2 text-lg font-semibold text-blue-900">Kognitive Stärkenanalyse</h3>
								<p class="text-blue-700">
									Bewertung von Gedächtnis, Konzentration, logischem Denken und
									Problemlösungsfähigkeiten
								</p>
							</div>

							<div
								class="p-6 transition-colors bg-white border border-blue-200 rounded-lg hover:border-blue-300"
							>
								<h3 class="mb-2 text-lg font-semibold text-blue-900">Stressursachen-Screening</h3>
								<p class="text-blue-700">
									Analyse von Faktoren, die Lernblockaden verursachen, einschließlich emotionaler
									und physiologischer Aspekte
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Results Section -->
			<section class="py-16 bg-gradient-to-b from-white to-green-50">
				<div class="container px-4 mx-auto">
					<div class="max-w-4xl mx-auto">
						<h2 class="mb-12 text-3xl font-bold text-center text-blue-900">
							Detaillierte Ergebnisberichte
						</h2>

						<div class="grid grid-cols-1 gap-8 md:grid-cols-2">
							<div
								class="p-8 border border-blue-200 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100"
							>
								<h3 class="mb-4 text-lg font-semibold text-blue-900">Personalisierte Analyse</h3>
								<ul class="space-y-3 text-blue-700">
									<li class="flex items-start">
										<AppIcon name="lucide:circle-arrow-right" class="w-5 h-5 mr-3 text-green-600" />
										<span>Detaillierte Lernprofilbeschreibung</span>
									</li>
									<li class="flex items-start">
										<AppIcon name="lucide:circle-arrow-right" class="w-5 h-5 mr-3 text-green-600" />
										<span>Identifizierte Stärken und Entwicklungsbereiche</span>
									</li>
									<li class="flex items-start">
										<AppIcon name="lucide:circle-arrow-right" class="w-5 h-5 mr-3 text-green-600" />
										<span>Psychometrische Messwerte und Interpretationen</span>
									</li>
								</ul>
							</div>

							<div
								class="p-8 border border-green-200 rounded-lg bg-gradient-to-br from-green-50 to-green-100"
							>
								<h3 class="mb-4 text-lg font-semibold text-blue-900">Praktische Empfehlungen</h3>
								<ul class="space-y-3 text-green-700">
									<li class="flex items-start">
										<AppIcon name="lucide:circle-arrow-right" class="w-5 h-5 mr-3 text-green-600" />
										<span>Evidenzbasierte Lernstrategien</span>
									</li>
									<li class="flex items-start">
										<AppIcon name="lucide:circle-arrow-right" class="w-5 h-5 mr-3 text-green-600" />
										<span>Spezifische Interventionsvorschläge</span>
									</li>
									<li class="flex items-start">
										<AppIcon name="lucide:circle-arrow-right" class="w-5 h-5 mr-3 text-green-600" />
										<span>Ressourcen für Eltern und Pädagogen</span>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Credentials Section -->
			<section class="py-16 border-t border-blue-200 bg-gradient-to-b from-green-50 to-blue-50">
				<div class="container px-4 mx-auto">
					<div class="max-w-4xl mx-auto text-center">
						<h2 class="mb-12 text-3xl font-bold text-blue-900">
							Vertrauenswürdigkeit und Standards
						</h2>

						<div class="grid grid-cols-1 gap-8 md:grid-cols-2">
							<div
								class="p-6 transition-colors bg-white border border-blue-200 rounded-lg hover:border-blue-300"
							>
								<div class="mb-2 text-2xl font-bold text-blue-600">15.000+</div>
								<p class="text-blue-700">Durchgeführte Analysen mit hoher Zufriedenheit</p>
							</div>

							<div
								class="p-6 transition-colors bg-white border border-green-200 rounded-lg hover:border-green-300"
							>
								<div class="mb-2 text-2xl font-bold text-green-600">DSGVO-konform</div>
								<p class="text-green-700">Vollständiger Datenschutz und Sicherheitsstandards</p>
							</div>

							<div
								class="p-6 transition-colors bg-white border border-blue-200 rounded-lg hover:border-blue-300"
							>
								<div class="mb-2 text-2xl font-bold text-blue-600">Von Pädagogen empfohlen</div>
								<p class="text-blue-700">Verwendet von Schulen und Lerntherapie-Zentren</p>
							</div>

							<div
								class="p-6 transition-colors bg-white border border-green-200 rounded-lg hover:border-green-300"
							>
								<div class="mb-2 text-2xl font-bold text-green-600">4.8/5 Sterne</div>
								<p class="text-green-700">Durchschnittliche Bewertung von Nutzern</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- FAQ Section - Using Accordion Component -->
			<Accordion
				title="Häufig gestellte Fragen"
				subtitle="Alles, was Sie über unseren 90-Fragen-Test wissen müssen"
				:items="faqItems"
				background-color="bg-white"
				:show-cta="true"
				cta-text="Bereit für Ihren 90-Fragen-Test? Starten Sie jetzt kostenlos!"
				@cta-click="startOnboarding"
			/>

			<!-- Final CTA Section - Conversion Bridge -->
			<section class="py-20 text-white bg-gradient-to-r from-blue-900 to-blue-800">
				<div class="container px-4 mx-auto">
					<div class="max-w-3xl mx-auto text-center">
						<h2 class="mb-6 text-4xl font-bold">Schluss mit Lernfrust und schlechten Noten!</h2>
						<p class="mb-8 text-xl text-blue-100">
							Starten Sie jetzt Ihren 90-Fragen-Test zur Lern-Optimierung und erhalten Sie sofort
							Ihren Lerntyp
						</p>

						<TrackClick
							event="final_cta_clicked"
							category="cta"
							cta-type="primary"
							cta-location="home_final"
							page-section="home_final"
							tag="button"
							class="inline-flex items-center px-10 py-5 text-lg font-bold text-white transition-colors transform bg-green-500 rounded-lg shadow-lg hover:bg-green-600 hover:shadow-2xl hover:scale-105"
							@click="startOnboarding"
						>
							<AppIcon name="heroicons-outline:arrow-right" class="w-6 h-6 mr-3 text-white" />
							<span>90-Fragen-Test starten (Gratis-Ergebnis sichern)!</span>
						</TrackClick>

						<p class="mt-6 text-sm text-blue-200">
							Kostenloser Basis Report • Keine Anmeldung • Sofortige Ergebnisse • Entwickelt von
							zertifizierten Lernpädagogen
						</p>
					</div>
				</div>
			</section>
		</div>
	</AppLayout>
</template>

<script setup lang="ts">
/**
 * HomeView - Landing page with smart flow detection
 *
 * Uses coordination layer to determine which UI to show:
 * - Fresh start: Landing hero
 * - Partial onboarding: Auto-resume onboarding
 * - Test in progress: Resume test banner
 * - Onboarding complete: Show hero with start button
 */

import { computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useOnboardingStore } from '@/stores/onboarding';
import { useWizardStore } from '@/stores/wizard';
import { useTestFlowCoordination } from '@/stores/coordination/test-flow';
import { useTestProgress } from '@/composables/useTestProgress';
import { useSeo, getCanonicalUrl, getAbsoluteImageUrl } from '@/composables/useSeo';
import AppLayout from '@/components/layout/AppLayout.vue';
import ResumeTestBanner from '@/components/common/ResumeTestBanner.vue';
import TrackClick from '@/components/tracking/TrackClick.vue';
import Accordion from '@/components/marketing/Accordion.vue';
import SampleReportCard from '@/components/marketing/SampleReportCard.vue';
import ConversionElements from '@/components/marketing/ConversionElements.vue';
import AppIcon from '@/components/icons/AppIcon.vue';

// ===========================
// COMPOSABLES
// ===========================

const router = useRouter();
const route = useRoute();
const onboarding = useOnboardingStore();
const wizard = useWizardStore();
const coordination = useTestFlowCoordination();
const { resumeProgress, getSavedProgress } = useTestProgress();

// ===========================
// SEO METADATA
// ===========================

// Apply SEO metadata from route meta or use custom configuration
const seoMeta = route.meta.seo || {
	title: 'Lerntypentest - 90 Fragen für echte Klarheit',
	description:
		'Entdecken Sie den Lerntyp Ihres Kindes mit unserem wissenschaftlich fundierten 90-Fragen-Test. Kostenlos, ohne Anmeldung, sofortige Ergebnisse.',
	keywords: 'Lerntypentest, Lerntyp Test, Kinder Lernen, Lerntypen, Vester Test, Lernstrategie',
	canonical: getCanonicalUrl('/'),
	openGraph: {
		title: 'Lerntypentest - 90 Fragen für echte Klarheit',
		description:
			'Entdecken Sie den Lerntyp Ihres Kindes mit unserem wissenschaftlich fundierten 90-Fragen-Test.',
		type: 'website',
		url: getCanonicalUrl('/'),
		image: getAbsoluteImageUrl('/images/og-home.jpg'),
	},
	twitter: {
		card: 'summary_large_image',
		title: 'Lerntypentest - 90 Fragen für echte Klarheit',
		description:
			'Entdecken Sie den Lerntyp Ihres Kindes mit unserem wissenschaftlich fundierten 90-Fragen-Test.',
		image: getAbsoluteImageUrl('/images/og-home.jpg'),
	},
	structuredData: {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'Lerntypentest',
		url: getCanonicalUrl('/'),
		description: 'Wissenschaftlich fundierter Lerntypentest für Kinder',
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: getCanonicalUrl('/') + '?q={search_term_string}',
			},
			'query-input': 'required name=search_term_string',
		},
	},
};

useSeo(seoMeta);

// ===========================
// STATE
// ===========================

// ===========================
// FAQ ITEMS
// ===========================

interface AccordionItem {
	question: string;
	answer: string;
	details?: string[];
}

const faqItems: AccordionItem[] = [
	{
		question: 'Ist der Test wirklich kostenlos?',
		answer:
			'Ja, absolut! Der 90-Fragen-Test und Ihr Lerntyp-Ergebnis sind 100% kostenlos. Sie erhalten sofort nach Abschluss des Tests Ihren Lerntyp und eine Grundanalyse. Keine versteckten Kosten, keine Anmeldung erforderlich.',
	},
	{
		question: 'Was erhalte ich nach den 90 Fragen?',
		answer:
			'Nach dem kostenlosen Test erhalten Sie Ihr Lerntyp-Ergebnis, grundlegende Empfehlungen für Ihren Lerntyp und die Option auf eine professionelle Analyse mit individuellen Empfehlungen von zertifizierten Lernpädagogen.',
		details: [
			'Ihr Lerntyp-Ergebnis (kostenlos)',
			'Grundlegende Empfehlungen für Ihren Lerntyp',
			'Option auf professionelle Analyse mit individuellen Empfehlungen',
		],
	},
	{
		question: 'Wie hilft die professionelle Analyse bei besseren Noten?',
		answer:
			'Die professionelle Analyse geht über den Lerntyp hinaus. Sie erhalten konkrete Strategien zur Bewältigung von Lernschwierigkeiten, optimierte Tipps für Klassenarbeiten-Vorbereitung und individuelle Lernstrategien, die auf Ihre spezifische Situation abgestimmt sind – entwickelt von zertifizierten Lernpädagogen.',
	},
	{
		question: 'Warum 90 Fragen statt 5 Minuten?',
		answer:
			'90 Fragen ermöglichen eine tiefere Analyse der gedanklichen Verknüpfungen und der Verarbeitung im Gehirn. Der klassische 5-Minuten-Vester-Test ist zu kurz für präzise Ergebnisse. Unsere 90 Fragen basieren auf aktuellen pädagogischen Standards und liefern Ihnen echte Klarheit statt oberflächlicher Ergebnisse.',
	},
	{
		question: 'Wer hat diesen Test entwickelt?',
		answer:
			'Der Test wurde von zertifizierten Lernpädagogen entwickelt und geprüft. Er basiert auf aktuellen Erkenntnissen der Lernpsychologie und kognitiven Neurowissenschaften. Über 15.000 Teilnehmer haben bereits den Test absolviert und profitieren von den Ergebnissen.',
	},
];

// ===========================
// COMPUTED
// ===========================

/**
 * Show resume banner if test is in progress or onboarding complete
 */
const showResumeBanner = computed(() => {
	return coordination.shouldShowResumeBanner.value;
});

// ===========================
// LIFECYCLE - Auto-Resume Logic
// ===========================

/**
 * Handle auto-resume after Pinia store hydration
 * Must wait for localStorage to load into stores
 */
onMounted(async () => {
	// Wait for Pinia persist to hydrate from localStorage
	await nextTick();
	await nextTick(); // Double nextTick ensures store is fully hydrated

	const flowState = coordination.flowState.value;
	console.log('Flow state after mount:', flowState);

	// Auto-resume onboarding if user was partway through
	if (flowState === 'partial-onboarding') {
		router.push('/onboarding');
		console.log('Auto-resuming onboarding from step', onboarding.currentStep);
	}
});

/**
 * Watch flow state changes for reactive updates
 * Handles cases where state changes after component is mounted
 */
watch(
	() => coordination.flowState.value,
	(newState, oldState) => {
		if (newState !== oldState) {
			console.log('Flow state changed:', oldState, '→', newState);
		}

		// If user starts onboarding in another tab, sync here
		if (newState === 'partial-onboarding') {
			router.push('/onboarding');
			console.log('Syncing onboarding display');
		}
	},
);

// ===========================
// ACTIONS
// ===========================

/**
 * Start onboarding flow
 * Clears any existing test data to start fresh
 */
const startOnboarding = () => {
	// If there's existing test data, clear it before starting fresh
	if (coordination.flowState.value !== 'fresh-start') {
		console.log('Clearing existing test data to start fresh');
		wizard.reset();
		onboarding.reset();
	}

	router.push('/onboarding');
};

/**
 * Resume test from where user left off
 */
function handleResumeTest() {
	// Get flow state to determine resume behavior
	const flowState = coordination.flowState.value;

	if (flowState === 'test-in-progress') {
		console.log(
			`Resuming test with ${coordination.answeredQuestionsCount.value}/${coordination.resumeOptions.value.totalQuestions} questions answered`,
		);
		// Navigate to wizard at current position
		router.push('/wizard/1');
	} else if (flowState === 'onboarding-complete') {
		// Onboarding complete but test not started yet - prepare test start
		console.log('Resuming from onboarding-complete state');
		if (coordination.prepareTestStart()) {
			router.push('/wizard/1');
		}
	} else if (flowState === 'partial-onboarding') {
		// Navigate to onboarding to continue
		router.push('/onboarding');
	} else {
		// Fallback to saved progress
		const saved = getSavedProgress();
		if (saved && resumeProgress()) {
			router.push('/wizard/1');
		}
	}
}

/**
 * Dismiss resume banner and optionally start fresh
 */
function handleDismissSaved() {
	// User can choose to start fresh from the banner
	// For now, just hide the banner - they can restart from onboarding
}

/**
 * Scroll to a specific section (used for secondary CTA)
 */
function scrollToSection() {
	// Scroll to the "Analysierte Dimensionen" section
	const element = document.querySelector('#Lerntypen Test Mehrwert');
	if (element) {
		element.scrollIntoView({ behavior: 'smooth', block: 'start' });
	}
}
</script>
